/**
 * SplashContactPage — 랜딩페이지 "문의" 탭 · 상담하기(비회원 챗봇 위젯)
 *
 * 참고: 고객지원_상담체계_화면기획_V1.6.html 좌측 메뉴 "비회원(로그인 전) > 상담하기".
 * FAQ 탭(SplashFaqPage)과 동일하게 D-07 §1.2 랜딩페이지 예외 대상이 아니다 — 표준 토큰·
 * ui-core 컴포넌트를 그대로 쓴다(raw 폼 요소 금지, §3.2 #6).
 *
 * 정형 시나리오 Q&A는 sv-chat(indiChat) 실 API로 동작한다(X-Anon-Session으로 비회원 식별,
 * visibility=GUEST는 서버가 결정). "1:1 문의 남기기"는 POST /api/chat/handoff에 접점정보를
 * 실어 제출한다(sv-support 실 연동 전까지는 indiChat이 스텁 처리). 급한 문의는 로그인 후 이용
 * 가능한 기존 1:1 문의(/cs/inquiry)로 안내한다. 후속 칩이 `@HANDOFF:*` 토큰이면 이 인라인
 * 폼을 그대로 연다.
 *
 * 개인정보 수집·이용 동의·허니팟은 고객상담센터_구현정의서_v1.0.html §4.2 FR-10 그대로 반영 —
 * 회원(StudioChatbotPage)은 가입 시 동의로 갈음해 노출하지 않고, 비회원(이 화면)만 필수 체크.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Avatar, Button, Checkbox, Input, Select, Textarea } from '@indipia/ui-core';
import {
  isHandoffFollowup, sendChatHandoff, sendChatMessage, startChatSession,
  type ChatFollowup, type ChatMessageInput,
} from '../api/chat';

interface QaTurn {
  id: number;
  question: string;
  answer: string | null; // null = 답변 대기(타이핑 중)
}

const INQUIRY_TYPES = ['요금·베타 문의', '가입·이용 방법', '지원·현장 사용', '기능 문의', '기타'];

const PHONE_REGEX = /^01[0-9]-\d{4}-\d{4}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 숫자 아닌 문자는 입력 즉시 제거하고, 자릿수에 맞춰 하이픈을 자동으로 붙인다(010-0000-0000 형식). */
function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function BotIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16" />
      <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 rounded-2xl rounded-tl-sm border border-border bg-surface px-3.5 py-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-disabled"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

export function SplashContactPage() {
  const navigate = useNavigate();
  const seqRef = useRef(0);
  const nextId = () => (seqRef.current += 1);

  const [turns, setTurns] = useState<QaTurn[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', type: INQUIRY_TYPES[0], content: '',
    consent: false, honeypot: '', // honeypot: 사람은 항상 비워둠 — 채워지면 봇 자동제출로 간주(FR-10)
  });
  const [submitting, setSubmitting] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  // 폼 안쪽 전용 오류 메시지 — 스레드 상단의 notice는 스크롤이 맨 아래로 붙어버려서(useEffect 참조)
  // 세로로 긴 폼이 열려 있을 때는 화면 밖으로 밀려 안 보인다. 폼 검증 오류는 반드시 폼 안,
  // 제출 버튼 바로 위에 렌더링해서 스크롤 위치와 무관하게 항상 보이게 한다.
  const [formError, setFormError] = useState<string | null>(null);

  const threadRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [welcomeAnswer, setWelcomeAnswer] = useState<string | null>(null);
  const [followups, setFollowups] = useState<ChatFollowup[]>([]);
  const rootsRef = useRef<ChatFollowup[]>([]);

  useEffect(() => {
    startChatSession()
      .then((res) => {
        setSessionId(res.sessionId);
        setWelcomeAnswer(res.welcome.answer);
        rootsRef.current = res.roots;
        setFollowups(res.roots);
      })
      .catch(() => {
        setWelcomeAnswer(
          '안녕하세요! 저는 인테리어 사장님을 돕는 AI 챗봇, 인디봇이에요.\n요금제·가입 방법·베타 이용 관련 질문에 답변드릴 수 있어요.',
        );
      });
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, notice]);

  // 폼은 세로로 길어서 스레드 맨 아래로 스크롤하면 폼 위쪽(제목·이름 입력)이 화면 밖으로 밀려난다 —
  // 열릴 때만 폼의 맨 위가 보이도록 별도로 스크롤한다. scrollIntoView는 상위 페이지 스크롤까지
  // 같이 움직여 상단 고정 네비게이션에 가려지므로, 챗봇 내부 스크롤(threadRef)만 직접 계산해 옮긴다.
  useEffect(() => {
    if (formOpen && formRef.current && threadRef.current) {
      const container = threadRef.current;
      const offset = formRef.current.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, [formOpen]);

  async function ask(input: ChatMessageInput, questionLabel: string) {
    if (!sessionId) return;
    const id = nextId();
    setNotice(null);
    setTurns((prev) => [...prev, { id, question: questionLabel, answer: null }]);
    try {
      const result = await sendChatMessage(sessionId, input);
      setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, answer: result.answer ?? result.prompt ?? '' } : t)));
      setFollowups(result.followups ?? result.candidates ?? rootsRef.current);
    } catch {
      setTurns((prev) => prev.map((t) => (
        t.id === id ? { ...t, answer: '일시적으로 답변이 어려워요. 다시 시도해 주세요.' } : t
      )));
    }
  }

  function onFollowupClick(f: ChatFollowup) {
    if (isHandoffFollowup(f.nodeCode)) { openForm(); return; }
    if (f.nodeCode === '@ROOTS') { setFollowups(rootsRef.current); return; }
    void ask({ type: 'QUICK_REPLY', nodeCode: f.nodeCode }, f.quickReply ?? f.nodeCode);
  }

  function handleSend() {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue('');
    void ask({ type: 'FREE_TEXT', text }, text);
  }

  function openForm() {
    setFormError(null);
    setFormOpen(true);
  }

  function closeForm() {
    setFormError(null);
    setFormOpen(false);
  }

  function validatePhoneField() {
    const v = form.phone.trim();
    setFormError(v && !PHONE_REGEX.test(v) ? '연락처는 010-0000-0000 형식으로 입력해 주세요' : null);
  }

  function validateEmailField() {
    const v = form.email.trim();
    setFormError(v && !EMAIL_REGEX.test(v) ? '이메일 형식이 올바르지 않아요' : null);
  }

  async function submitInquiry() {
    if (!sessionId || submitting || inquirySubmitted) return;
    if (!form.name.trim() || !form.phone.trim() || !form.content.trim()) {
      setFormError('이름·연락처·문의 내용은 필수예요');
      return;
    }
    if (!PHONE_REGEX.test(form.phone.trim())) {
      setFormError('연락처는 010-0000-0000 형식으로 입력해 주세요');
      return;
    }
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      setFormError('이메일 형식이 올바르지 않아요');
      return;
    }
    if (!form.consent) {
      setFormError('개인정보 수집·이용에 동의해 주세요');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      await sendChatHandoff(sessionId, {
        contact: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          inquiryType: form.type,
          content: form.content.trim() || undefined,
          privacyConsent: form.consent,
          honeypot: form.honeypot || undefined,
        },
      });
      setInquirySubmitted(true);
      setFormOpen(false);
      setNotice('문의가 접수됐어요. 확인 후 순서대로 회신드릴게요.');
    } catch {
      setFormError('접수에 실패했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  }

  function resetToQuickReplies() {
    setFormError(null);
    setFormOpen(false);
    setFollowups(rootsRef.current);
    setNotice('다른 질문을 골라주세요');
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10 pb-16">
      {/* 헤더 */}
      <div className="mb-6 text-center">
        <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-widest text-point">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-point" />
          AI 챗봇 상담
        </p>
        <h2 className="mb-1 text-xl font-bold text-text">24시간 언제든, 챗봇에게 물어보세요</h2>
        <p className="text-xs text-text-muted">
          궁금한 점을 인디봇에게 편하게 물어보세요. 답을 찾기 어려우면 "1:1 문의 남기기"로 남겨주세요.
        </p>
      </div>

      {/* 챗봇 위젯 */}
      <div className="overflow-hidden rounded-2xl border border-border bg-surface-raised">
        {/* 위젯 헤더 */}
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Avatar aria-hidden>
            <BotIcon />
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-sm font-bold text-text">
              인디봇
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
            </p>
            <p className="text-xs text-text-muted">AI 챗봇이 답변해요 · 평균 3초 이내 응답</p>
          </div>
        </div>

        {/* 대화 영역 */}
        <div ref={threadRef} className="flex max-h-[480px] flex-col gap-3 overflow-y-auto bg-surface-subtle px-5 py-5">
          {/* 첫 인사 — 세션 개시 응답의 웰컴 노드(sv-chat) */}
          <div className="flex items-start gap-2.5">
            <Avatar aria-hidden className="!h-7 !w-7 shrink-0">
              <span className="scale-75"><BotIcon /></span>
            </Avatar>
            {welcomeAnswer === null ? (
              <TypingDots />
            ) : (
              <div className="flex max-w-[85%] flex-col gap-1.5">
                <div className="whitespace-pre-line rounded-2xl rounded-tl-sm border border-border bg-surface px-3.5 py-2.5 text-sm leading-relaxed text-text">
                  {welcomeAnswer}
                </div>
                <p className="text-xs leading-relaxed text-text-disabled">
                  AI 챗봇 답변은 부정확할 수 있어요. 정확한 안내가 필요하면 아래 "1:1 문의 남기기"를 이용해 주세요.
                </p>
              </div>
            )}
          </div>

          {/* 진행된 대화 */}
          {turns.map((turn) => (
            <div key={turn.id} className="flex flex-col gap-3">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-point px-3.5 py-2.5 text-sm font-medium text-white">
                  {turn.question}
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Avatar aria-hidden className="!h-7 !w-7 shrink-0">
                  <span className="scale-75"><BotIcon /></span>
                </Avatar>
                {turn.answer === null ? (
                  <TypingDots />
                ) : (
                  <div className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-sm border border-border bg-surface px-3.5 py-2.5 text-sm leading-relaxed text-text">
                    {turn.answer}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* 시스템 안내 */}
          {notice && <p className="text-center text-xs text-text-muted">{notice}</p>}

          {/* 1:1 문의 남기기 인라인 폼 */}
          {formOpen && (
            <div ref={formRef} className="flex items-start gap-2.5">
              <Avatar aria-hidden className="!h-7 !w-7 shrink-0">
                <span className="scale-75"><BotIcon /></span>
              </Avatar>
              <div className="flex-1 rounded-2xl rounded-tl-sm border border-border bg-surface p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-bold text-text">1:1 문의 남기기</p>
                  <Button type="button" variant="etc" size="sm" onClick={closeForm} aria-label="문의 폼 닫기" className="!h-6 !w-6 !p-0">
                    ×
                  </Button>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <Input
                      label="이름"
                      required
                      size="sm"
                      width="fill"
                      value={form.name}
                      onChange={(e) => { setFormError(null); setForm((f) => ({ ...f, name: e.target.value })); }}
                      placeholder="홍길동"
                    />
                    <Input
                      label="연락처"
                      required
                      size="sm"
                      width="fill"
                      type="tel"
                      inputMode="numeric"
                      value={form.phone}
                      onChange={(e) => { setFormError(null); setForm((f) => ({ ...f, phone: formatPhoneInput(e.target.value) })); }}
                      onBlur={validatePhoneField}
                      placeholder="010-0000-0000"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Input
                      label="이메일 (선택)"
                      size="sm"
                      width="fill"
                      type="email"
                      value={form.email}
                      onChange={(e) => { setFormError(null); setForm((f) => ({ ...f, email: e.target.value })); }}
                      onBlur={validateEmailField}
                      placeholder="example@email.com"
                    />
                    <Select
                      label="문의 유형"
                      size="sm"
                      className="!max-w-none flex-1"
                      value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    >
                      {INQUIRY_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Select>
                  </div>
                  <Textarea
                    label="문의 내용 (필수)"
                    className="!max-w-none"
                    rows={3}
                    value={form.content}
                    onChange={(e) => { setFormError(null); setForm((f) => ({ ...f, content: e.target.value })); }}
                    placeholder="궁금하신 내용을 자유롭게 적어주세요"
                  />
                  <Checkbox
                    label={
                      <>
                        개인정보 수집·이용에 동의합니다 (필수) — 문의 접수·회신 목적으로만 사용해요.{' '}
                        <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--gt-primary)' }}>자세히</a>
                      </>
                    }
                    checked={form.consent}
                    onChange={(e) => { setFormError(null); setForm((f) => ({ ...f, consent: e.target.checked })); }}
                  />
                  {/* 허니팟 — 사람 눈엔 안 보임(autoComplete=off·tabIndex=-1·시각적 은닉). 값이 채워지면 봇 자동제출로 간주(FR-10) */}
                  <label
                    htmlFor="cb-inquiry-website"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
                  >
                    웹사이트(입력하지 마세요)
                    <input
                      id="cb-inquiry-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.honeypot}
                      onChange={(e) => setForm((f) => ({ ...f, honeypot: e.target.value }))}
                    />
                  </label>
                </div>

                <div className="mt-3.5 border-t border-border pt-3.5">
                  {formError && (
                    <p className="mb-2 text-center text-xs font-semibold text-red-500">{formError}</p>
                  )}
                  <Button type="button" variant="primary" size="sm" onClick={submitInquiry} disabled={submitting || !form.consent} className="w-full">
                    {submitting ? '접수 중…' : '문의 접수'}
                  </Button>
                  <p className="mt-2 text-xs leading-relaxed text-text-muted">
                    급한 문의는 로그인 후 1:1 문의를 이용해 주세요.
                  </p>
                  <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/login')} className="mt-1 !px-0">
                    로그인하고 1:1 문의하기 →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 퀵리플라이 — 문의 폼이 열려 있는 동안에는 노출 항목이 다르다 */}
          <div className="flex flex-wrap gap-2 pl-9">
            {!formOpen ? (
              <>
                {followups.map((f, idx) => (
                  <Button
                    key={`${f.nodeCode}-${idx}`}
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onFollowupClick(f)}
                    className="!rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
                  >
                    {f.quickReply ?? f.nodeCode}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={openForm}
                  disabled={inquirySubmitted}
                  className="!rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
                >
                  1:1 문의 남기기
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={resetToQuickReplies}
                  className="!rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
                >
                  다른 질문할게요
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled
                  className="!rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
                >
                  1:1 문의 남기기
                </Button>
              </>
            )}
          </div>
        </div>

        {/* 입력창 */}
        <div className="flex items-center gap-2 border-t border-border bg-surface-raised px-4 py-3">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            placeholder="궁금한 점을 입력해 주세요"
            disabled={!sessionId}
            size="sm"
            width="fill"
            aria-label="챗봇에게 질문 입력"
          />
          <Button
            type="button" variant="primary" size="sm" onClick={handleSend} aria-label="전송"
            disabled={!sessionId}
            className="!h-9 !w-9 !rounded-full !p-0"
          >
            <SendIcon />
          </Button>
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-text-muted">
        챗봇이 원하는 답을 찾지 못했다면 위의 "1:1 문의 남기기"를 눌러주세요.
      </p>
    </div>
  );
}
